/* eslint-disable no-console */
import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Button,
  TextField,
  Tooltip,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Switch,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { db } from "../firebase";
import { updateRankings } from "./Rankings";

// -------------------- Helper Functions --------------------
function parseDateStr(dateStr) {
  if (!dateStr) return new Date();
  const parts = dateStr.split("/");
  if (parts.length < 3) return new Date();
  const [day, month, year] = parts.map(Number);
  if (!day || !month || !year) return new Date();
  return new Date(year, month - 1, day);
}

function groupMatchesByTeamPair(matches = []) {
  const grouped = {};
  matches.forEach((m) => {
    // Use m.team1TeamName/m.team2TeamName or fallback
    const team1Key = m.team1TeamName || "Team1";
    const team2Key = m.team2TeamName || "Team2";
    const pairKey = `${team1Key}##${team2Key}`;
    if (!grouped[pairKey]) {
      grouped[pairKey] = [];
    }
    grouped[pairKey].push(m);
  });
  return grouped;
}

function groupPlayersBySection(matchGroup) {
  const sections = {};
  matchGroup.forEach((match) => {
    const sec = match.section || "NoSection";
    if (!sections[sec]) {
      sections[sec] = [];
    }
    sections[sec].push(match);
  });
  return sections;
}

function isValidSetScore(s1, s2, ppg, tieBreakerRule) {
  // Example logic: if "First to X" or "win by 2", etc.
  // Adjust as needed for your scoring rules.

  if (s1 === 0 && s2 === 0) return true;

  if (tieBreakerRule.includes("First to X")) {
    // If both are >= ppg, invalid
    if (s1 >= ppg && s2 >= ppg) return false;
    return true;
  }

  if (tieBreakerRule.includes("win by 2")) {
    if (s1 >= ppg || s2 >= ppg) {
      if (Math.abs(s1 - s2) < 2) return false;
    }
    return true;
  }

  // Basic clamp
  if (s1 > ppg || s2 > ppg) return false;
  return true;
}

function checkIfCurrentSetIsAllowed(scoresRow, setIndex) {
  // If setIndex is 0, always allowed
  if (setIndex === 0) return true;
  const pids = Object.keys(scoresRow);
  if (pids.length !== 2) return true;

  // Must have non-empty values in the previous set
  const [pA, pB] = pids;
  const prevA = scoresRow[pA][setIndex - 1];
  const prevB = scoresRow[pB][setIndex - 1];
  if (prevA === undefined || prevA === "" || prevB === undefined || prevB === "") {
    return false;
  }
  return true;
}

function checkIfSideIsDone(team1Scores, team2Scores, scoringType, side) {
  // Example logic: if the team has already won a "bestOf" format
  // If your scoring is different, adapt as needed.
  const { matchFormat = "", pointsPerGame = "21" } = scoringType;
  const ppg = parseInt(pointsPerGame, 10) || 21;

  // Attempt to parse "best of X"
  let bestOfN = 0;
  let isPlayAll = true;
  const match = matchFormat.match(/best\s*of\s*(\d+)/i);
  if (match && match[1]) {
    const n = parseInt(match[1], 10);
    if (!Number.isNaN(n) && n > 0) {
      bestOfN = n;
      isPlayAll = false;
    }
  }

  let t1SetsWon = 0;
  let t2SetsWon = 0;
  for (let i = 0; i < team1Scores.length; i += 1) {
    const s1 = team1Scores[i];
    const s2 = team2Scores[i];
    if (s1 === 0 && s2 === 0) {
      // skip
    } else if (s1 > s2) {
      t1SetsWon += 1;
    } else if (s2 > s1) {
      t2SetsWon += 1;
    }

    if (!isPlayAll) {
      const required = Math.ceil(bestOfN / 2);
      if (t1SetsWon >= required || t2SetsWon >= required) {
        break;
      }
    }
  }

  if (side === "team1") {
    return t1SetsWon >= Math.ceil(bestOfN / 2);
  }
  return t2SetsWon >= Math.ceil(bestOfN / 2);
}

function computeTeamMatchPoints(pointsCalculation, setsCount, { team1Scores, team2Scores }, scoringType) {
  const { matchFormat = "", pointsPerGame = "21" } = scoringType;
  const ppg = parseInt(pointsPerGame, 10) || 21;

  // Example logic for "Per Rally", "Per Game", "Per Match"
  if (pointsCalculation === "Per Rally") {
    const sumT1 = team1Scores.reduce((a, b) => a + b, 0);
    const sumT2 = team2Scores.reduce((a, b) => a + b, 0);
    return { team1Points: sumT1, team2Points: sumT2 };
  }

  if (pointsCalculation === "Per Game") {
    // We'll count how many sets each side won
    let t1SetsWon = 0;
    let t2SetsWon = 0;
    for (let i = 0; i < setsCount; i += 1) {
      const s1 = team1Scores[i];
      const s2 = team2Scores[i];
      if (s1 > s2) t1SetsWon += 1;
      else if (s2 > s1) t2SetsWon += 1;
    }
    return { team1Points: t1SetsWon, team2Points: t2SetsWon };
  }

  if (pointsCalculation === "Per Match") {
    let sumT1 = 0;
    let sumT2 = 0;
    for (let i = 0; i < setsCount; i += 1) {
      sumT1 += team1Scores[i];
      sumT2 += team2Scores[i];
    }
    if (sumT1 > sumT2) return { team1Points: 1, team2Points: 0 };
    if (sumT2 > sumT1) return { team1Points: 0, team2Points: 1 };
    return { team1Points: 0, team2Points: 0 };
  }

  // Fallback
  return { team1Points: 0, team2Points: 0 };
}

// -------------------- Main Component --------------------
export default function TeamsScoreSheet({ pennantId, weekData }) {
  const [pennantData, setPennantData] = useState(null);
  const [scores, setScores] = useState({}); // { uniqueKey: { p1Id: { setIndex: number }, p2Id: {...} } }
  const [matchPoints, setMatchPoints] = useState({}); // { uniqueKey: { team1, team2, isManual } }
  const [saving, setSaving] = useState(false);

  // -------- 1) Load Pennant Doc --------
  useEffect(() => {
    if (!pennantId) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "Pennants", pennantId));
        if (snap.exists()) {
          setPennantData(snap.data());
        }
      } catch (err) {
        console.error("Error fetching pennant data:", err);
      }
    })();
  }, [pennantId]);

  // -------- 2) Load existing Matches for the week --------
  useEffect(() => {
    if (!weekData?.id || !pennantId) return;
    (async () => {
      try {
        setScores({});
        setMatchPoints({});

        const mQuery = query(
          collection(db, "Matches"),
          where("pennantId", "==", pennantId),
          where("weekId", "==", weekData.id)
        );
        const snap = await getDocs(mQuery);

        const loadedScores = {};
        const loadedPoints = {};

        snap.forEach((docSnap) => {
          const mData = docSnap.data();
          const docId = docSnap.id;
          const { team1, team2 } = mData;
          if (!team1 || !team2) return;

          loadedScores[docId] = {
            [team1.id]: team1.scores || {},
            [team2.id]: team2.scores || {},
          };

          loadedPoints[docId] = {
            team1: team1.matchPoints || 0,
            team2: team2.matchPoints || 0,
            isManual: true,
          };
        });

        setScores(loadedScores);
        setMatchPoints(loadedPoints);
      } catch (err) {
        console.error("Error fetching match docs:", err);
      }
    })();
  }, [pennantId, weekData]);

  // -------- 3) Scoring config from pennant --------
  let pointsPerGameStr = "21";
  let gamesPerSetStr = "3";
  let matchFormat = "Best of 3";
  let tieBreakerRule = "";
  let scoringSystem = "Rally Point";
  let pointsCalculation = "Per Game";

  if (pennantData && pennantData.scoringType) {
    const st = pennantData.scoringType;
    pointsPerGameStr = st.pointsPerGame || "21";
    gamesPerSetStr = st.gamesPerSet || "3";
    matchFormat = st.matchFormat || "Best of 3";
    tieBreakerRule = st.tieBreakerRule || "";
    scoringSystem = st.scoringSystem || "Rally Point";
    pointsCalculation = st.pointsCalculation || "Per Game";
  }

  const ppg = parseInt(pointsPerGameStr, 10) || 21;
  const gamesPerSetNum = parseInt(gamesPerSetStr, 10) || 3;
  const setsCount = Number.isNaN(gamesPerSetNum) ? 3 : gamesPerSetNum;

  // -------- 4) getMaxMatchPoints --------
  const getMaxMatchPoints = useCallback(() => {
    if (pointsCalculation === "Per Game" || pointsCalculation === "Per Set") {
      return setsCount;
    }
    if (pointsCalculation === "Per Rally") {
      return setsCount * ppg;
    }
    if (pointsCalculation === "Per Match") {
      return 1;
    }
    return setsCount;
  }, [pointsCalculation, setsCount, ppg]);

  // -------- 5) Recalc auto match points --------
  const recalcMatchPoints = useCallback(
    (uniqueKey, updatedScores) => {
      const rowScores = updatedScores[uniqueKey] || {};
      const pids = Object.keys(rowScores);
      if (pids.length !== 2) {
        return { team1Points: 0, team2Points: 0 };
      }

      const [p1Id, p2Id] = pids;
      const team1Array = [];
      const team2Array = [];

      for (let i = 0; i < setsCount; i += 1) {
        const v1 = rowScores[p1Id][i] || 0;
        const v2 = rowScores[p2Id][i] || 0;
        team1Array.push(v1);
        team2Array.push(v2);
      }

      return computeTeamMatchPoints(
        pointsCalculation,
        setsCount,
        { team1Scores: team1Array, team2Scores: team2Array },
        {
          pointsPerGame: ppg.toString(),
          tieBreakerRule,
          matchFormat,
        }
      );
    },
    [pointsCalculation, setsCount, ppg, tieBreakerRule, matchFormat]
  );

  // -------- 6) Handlers --------
  // (a) handleManualToggle
  const handleManualToggle = useCallback(
    (uniqueKey, isManual) => {
      setMatchPoints((prev) => {
        const newObj = {
          ...(prev[uniqueKey] || {}),
          isManual,
        };
        if (!isManual) {
          // recalc from existing scores
          const mp = recalcMatchPoints(uniqueKey, scores);
          newObj.team1 = mp.team1Points;
          newObj.team2 = mp.team2Points;
        }
        return { ...prev, [uniqueKey]: newObj };
      });
    },
    [scores, recalcMatchPoints]
  );

  // (b) handleScoreChange
  const handleScoreChange = useCallback(
    (uniqueKey, playerId, setIndex, rawValue, otherId) => {
      // The snippet has logic for negative, check if set is allowed, tie-break validity, etc.
      const val = parseInt(rawValue, 10);
      if (!Number.isNaN(val) && val < 0) {
        alert("Score cannot be negative!");
        return;
      }

      setScores((prev) => {
        const copy = { ...prev };
        if (!copy[uniqueKey]) copy[uniqueKey] = {};
        if (!copy[uniqueKey][playerId]) copy[uniqueKey][playerId] = {};

        // Ensure the other side is present
        if (otherId && !copy[uniqueKey][otherId]) {
          copy[uniqueKey][otherId] = {};
        }

        // Must fill the previous set first
        if (!checkIfCurrentSetIsAllowed(copy[uniqueKey], setIndex)) {
          alert("You must fill in the previous set first!");
          return prev;
        }

        const rowData = copy[uniqueKey];
        const [p1Id, p2Id] = Object.keys(rowData);
        const isTeam1 = p1Id === playerId;

        // Convert row data to arrays
        const t1Arr = [];
        const t2Arr = [];
        for (let i = 0; i < setsCount; i += 1) {
          t1Arr.push(rowData[p1Id]?.[i] || 0);
          t2Arr.push(rowData[p2Id]?.[i] || 0);
        }

        // Check if that side is done
        if (
          checkIfSideIsDone(
            isTeam1 ? t1Arr : t2Arr,
            isTeam1 ? t2Arr : t1Arr,
            pennantData.scoringType,
            isTeam1 ? "team1" : "team2"
          )
        ) {
          alert("This side already won the best-of limit.");
          return prev;
        }

        // Update the newly typed score
        if (rawValue === "") {
          copy[uniqueKey][playerId][setIndex] = "";
        } else if (!Number.isNaN(val)) {
          // clamp if needed
          if (val > ppg) {
            alert(`Score cannot exceed ${ppg}.`);
            return prev;
          }
          copy[uniqueKey][playerId][setIndex] = val;

          // tie-break checks
          const rowPids = Object.keys(copy[uniqueKey]);
          if (rowPids.length === 2) {
            const [idA, idB] = rowPids;
            const s1 = copy[uniqueKey][idA][setIndex] || 0;
            const s2 = copy[uniqueKey][idB][setIndex] || 0;
            if (!isValidSetScore(s1, s2, ppg, tieBreakerRule)) {
              alert("Invalid set score => tie-break violation");
              copy[uniqueKey][playerId][setIndex] = 0;
            }
          }
        }

        // Recalc match points in auto mode
        const mp = recalcMatchPoints(uniqueKey, copy);
        setMatchPoints((oldMp) => ({
          ...oldMp,
          [uniqueKey]: {
            team1: mp.team1Points,
            team2: mp.team2Points,
            // We do not track isManual toggles here, so it remains unchanged
            isManual: oldMp[uniqueKey]?.isManual ?? true,
          },
        }));
        return copy;
      });
    },
    [ppg, tieBreakerRule, setsCount, pennantData, recalcMatchPoints]
  );

  // (c) calculateTotal
  const calculateTotal = useCallback(
    (uniqueKey, playerId) => {
      const row = scores[uniqueKey] || {};
      const setsObj = row[playerId] || {};
      return Object.values(setsObj).reduce((acc, val) => acc + (val || 0), 0);
    },
    [scores]
  );

  // (d) handleMatchPointChange (manual mode)
  const handleMatchPointChange = useCallback(
    (uniqueKey, whichTeam, rawValue) => {
      let val = parseInt(rawValue, 10) || 0;
      const maxPoints = getMaxMatchPoints();
      if (val > maxPoints) {
        val = maxPoints;
      }
      setMatchPoints((oldMp) => ({
        ...oldMp,
        [uniqueKey]: {
          ...(oldMp[uniqueKey] || {}),
          [whichTeam]: val,
        },
      }));
    },
    [getMaxMatchPoints]
  );

  // -------- 7) Save Scores & Update Rankings --------
  const saveScores = useCallback(async () => {
    if (!weekData?.id || !Array.isArray(weekData.matches)) {
      console.error("weekData is invalid:", weekData);
      return;
    }
    setSaving(true);

    try {
      const groupedPairs = groupMatchesByTeamPair(weekData.matches);
      const matchDataArray = [];
      const allSaves = [];

      Object.entries(groupedPairs).forEach(([pairKey, matchGroup], pairIdx) => {
        const sectionsMap = groupPlayersBySection(matchGroup);
        Object.entries(sectionsMap).forEach(([secKey, theseMatches], sectionIdx) => {
          theseMatches.forEach((m, rowIndex) => {
            const uniqueKey = `${weekData.id}_${pairKey}_${secKey}_${pairIdx}-${sectionIdx}-${rowIndex}`;
            const rowData = scores[uniqueKey] || {};
            const docPoints = matchPoints[uniqueKey] || { team1: 0, team2: 0 };

            // pids => [p1Id, p2Id]
            const pids = Object.keys(rowData);
            if (pids.length !== 2) {
              console.warn("Skipping match due to insufficient players:", rowData);
              return;
            }

            const [p1Id, p2Id] = pids;
            // fill sets
            const t1Sets = rowData[p1Id] || {};
            const t2Sets = rowData[p2Id] || {};
            for (let i = 0; i < setsCount; i += 1) {
              if (!t1Sets[i]) t1Sets[i] = 0;
              if (!t2Sets[i]) t2Sets[i] = 0;
            }

            const total1 = calculateTotal(uniqueKey, p1Id);
            const total2 = calculateTotal(uniqueKey, p2Id);

            const matchDoc = {
              pennantId,
              pennantName: pennantData?.name || "UnknownTeamPennant",
              sportType: pennantData?.sportType || "Team Sport",
              seasonId: pennantData?.seasonName || "UnknownSeason",
              weekId: weekData.id,
              matchIndex: m.matchIndex || 0,
              matchDate: Timestamp.fromDate(parseDateStr(weekData.date || "")),
              section: m.section || null,
              team1: {
                id: p1Id,
                name: m.team1?.name || "Team1",
                parentTeamName: m.team1?.parentTeamName || "Team1",
                playerIds: m.team1?.playerIds || [],
                scores: t1Sets,
                total: total1,
                matchPoints: docPoints.team1,
              },
              team2: {
                id: p2Id,
                name: m.team2?.name || "Team2",
                parentTeamName: m.team2?.parentTeamName || "Team2",
                playerIds: m.team2?.playerIds || [],
                scores: t2Sets,
                total: total2,
                matchPoints: docPoints.team2,
              },
            };

            matchDataArray.push(matchDoc);

            const docRef = doc(db, "Matches", uniqueKey);
            allSaves.push(setDoc(docRef, matchDoc, { merge: true }));
          });
        });
      });

      // Save
      await Promise.all(allSaves);

      // Elo updates
      const penName = pennantData?.name || "UnknownTeamPennant";
      await updateRankings(matchDataArray, penName);

      alert("Team scores saved successfully!");
    } catch (err) {
      console.error("Error saving team scores:", err);
      alert("Error saving team scores.");
    } finally {
      setSaving(false);
    }
  }, [
    weekData,
    matchPoints,
    scores,
    setsCount,
    pennantData,
    pennantId,
    calculateTotal,
  ]);

  // -------- 8) Render --------
  if (!pennantData) {
    return <Typography>Loading Teams Score Sheet...</Typography>;
  }
  if (!weekData?.matches) {
    return <Typography>No matches found.</Typography>;
  }

  const groupedByPairs = groupMatchesByTeamPair(weekData.matches);

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h4" gutterBottom>
        {pennantData.name} - TEAMS
      </Typography>
      <Typography>Date: {weekData?.date || "Unknown Date"}</Typography>
      <Typography variant="h6" sx={{ mt: 2 }}>
        Scoring System: {scoringSystem} (Up to {ppg} points)
      </Typography>
      <Typography variant="subtitle1">Games Per Set: {gamesPerSetStr}</Typography>
      <Typography variant="subtitle1">Match Format: {matchFormat}</Typography>
      <Typography variant="subtitle1">Tie Breaker: {tieBreakerRule}</Typography>
      <Typography variant="subtitle1">
        Points Calculation: {pointsCalculation}
      </Typography>

      {Object.entries(groupedByPairs).map(([pairKey, matchGroup], pairIdx) => {
        const [team1Name, team2Name] = pairKey.split("##");
        const sectionsMap = groupPlayersBySection(matchGroup);

        return (
          <Accordion key={pairKey} sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                {team1Name} vs {team2Name}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {Object.entries(sectionsMap).map(([secKey, theseMatches], sectionIdx) => (
                <Box key={secKey} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Section: {secKey}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          {Array.from({ length: setsCount }, (_, i) => i + 1).map((n) => (
                            <TableCell align="center" key={n}>
                              Set {n}
                            </TableCell>
                          ))}
                          <TableCell align="center">Total</TableCell>
                          <TableCell align="center">Match Points</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {theseMatches.map((m, rowIndex) => {
                          const uniqueKey = `${weekData.id}_${pairKey}_${secKey}_${pairIdx}-${sectionIdx}-${rowIndex}`;
                          const t1Id = m.team1?.id || `t1Row${rowIndex}`;
                          const t2Id = m.team2?.id || `t2Row${rowIndex}`;
                          const disp1 = m.team1?.name || "Team1";
                          const disp2 = m.team2?.name || "Team2";

                          return (
                            <React.Fragment key={uniqueKey}>
                              {/* Manual / Auto Toggle */}
                              <TableRow>
                                <TableCell colSpan={setsCount + 3}>
                                  <Box display="flex" alignItems="center">
                                    <Switch
                                      checked={matchPoints[uniqueKey]?.isManual ?? true}
                                      onChange={(e) =>
                                        handleManualToggle(uniqueKey, e.target.checked)
                                      }
                                      color="primary"
                                    />
                                    <Typography variant="body2">
                                      {matchPoints[uniqueKey]?.isManual ?? true
                                        ? "Manual Match Points"
                                        : "Auto-calculate Match Points"}
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>

                              {/* Team1 row */}
                              <TableRow>
                                <TableCell>{disp1}</TableCell>
                                {Array.from({ length: setsCount }, (_, sIdx) => sIdx).map(
                                  (sIdx) => (
                                    <TableCell align="center" key={sIdx}>
                                      <Tooltip title={`Score for ${disp1}`}>
                                        <TextField
                                          type="number"
                                          variant="outlined"
                                          size="small"
                                          sx={{ width: 60 }}
                                          value={scores[uniqueKey]?.[t1Id]?.[sIdx] ?? 0}
                                          onChange={(e) =>
                                            handleScoreChange(
                                              uniqueKey,
                                              t1Id,
                                              sIdx,
                                              e.target.value,
                                              t2Id
                                            )
                                          }
                                        />
                                      </Tooltip>
                                    </TableCell>
                                  )
                                )}
                                <TableCell align="center">
                                  <TextField
                                    disabled
                                    variant="outlined"
                                    size="small"
                                    sx={{ width: 60 }}
                                    value={calculateTotal(uniqueKey, t1Id)}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <TextField
                                    type="number"
                                    variant="outlined"
                                    size="small"
                                    sx={{ width: 60 }}
                                    value={matchPoints[uniqueKey]?.team1 || 0}
                                    onChange={(e) =>
                                      handleMatchPointChange(uniqueKey, "team1", e.target.value)
                                    }
                                  />
                                </TableCell>
                              </TableRow>

                              {/* Team2 row */}
                              <TableRow>
                                <TableCell>{disp2}</TableCell>
                                {Array.from({ length: setsCount }, (_, sIdx) => sIdx).map(
                                  (sIdx) => (
                                    <TableCell align="center" key={sIdx}>
                                      <Tooltip title={`Score for ${disp2}`}>
                                        <TextField
                                          type="number"
                                          variant="outlined"
                                          size="small"
                                          sx={{ width: 60 }}
                                          value={scores[uniqueKey]?.[t2Id]?.[sIdx] ?? 0}
                                          onChange={(e) =>
                                            handleScoreChange(
                                              uniqueKey,
                                              t2Id,
                                              sIdx,
                                              e.target.value,
                                              t1Id
                                            )
                                          }
                                        />
                                      </Tooltip>
                                    </TableCell>
                                  )
                                )}
                                <TableCell align="center">
                                  <TextField
                                    disabled
                                    variant="outlined"
                                    size="small"
                                    sx={{ width: 60 }}
                                    value={calculateTotal(uniqueKey, t2Id)}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <TextField
                                    type="number"
                                    variant="outlined"
                                    size="small"
                                    sx={{ width: 60 }}
                                    value={matchPoints[uniqueKey]?.team2 || 0}
                                    onChange={(e) =>
                                      handleMatchPointChange(uniqueKey, "team2", e.target.value)
                                    }
                                  />
                                </TableCell>
                              </TableRow>

                              {/* Divider */}
                              <TableRow>
                                <TableCell colSpan={setsCount + 3}>
                                  <hr style={{ borderTop: "1px solid #ccc" }} />
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Box sx={{ textAlign: "center", mt: 3 }}>
        <Button variant="contained" onClick={saveScores} disabled={saving}>
          {saving ? "Saving..." : "Save Scores"}
        </Button>
      </Box>
    </Box>
  );
}

TeamsScoreSheet.propTypes = {
  pennantId: PropTypes.string.isRequired,
  weekData: PropTypes.shape({
    id: PropTypes.string,
    date: PropTypes.string,
    matches: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
};
